import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Radio, LogOut, MapPin, Truck, Bell, Navigation, CheckCircle2, XCircle, History, Package } from "lucide-react";
import { toast } from "sonner";
import { roadsideStatusLabel } from "@/lib/labels";
import { TRANSPORT_REQUEST_STATUS_TR } from "@/lib/labels";

const DRIVER_NEXT_STATUS = {
  OFFER_ACCEPTED: { key: "AWAITING_PICKUP", label: "Alıma Gidiyorum" },
  AWAITING_PICKUP: { key: "PICKED_UP", label: "Yükü Aldım" },
  PICKED_UP: { key: "IN_TRANSIT", label: "Taşımaya Başla" },
  IN_TRANSIT: { key: "DELIVERED", label: "Teslim Ettim" },
};

export default function DriverPage() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [online, setOnline] = useState(false);
  const [lastLoc, setLastLoc] = useState(null);
  const [lastAt, setLastAt] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [activeMission, setActiveMission] = useState(null);
  const [transportJobs, setTransportJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const pollRef = useRef(null);
  const gpsWatchdogRef = useRef(null);
  const lastPushRef = useRef(0);
  const lastIncomingIdRef = useRef(null);

  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      o.start(); o.stop(ctx.currentTime + 0.6);
    } catch {}
  };

  const pollRequests = async () => {
    try {
      const [pending, active, tjobs] = await Promise.all([
        api.get("/driver/help-requests/pending").then((r) => r.data).catch(() => []),
        api.get("/driver/help-requests/active").then((r) => r.data).catch(() => null),
        api.get("/driver/transport-jobs").then((r) => r.data).catch(() => []),
      ]);
      setActiveMission(active);
      // Idempotent alert per job: only ring for jobs we've never seen before (localStorage-backed).
      let seen = new Set();
      try { seen = new Set(JSON.parse(localStorage.getItem("yh_seen_tjobs") || "[]")); } catch { /* silent */ }
      const newOnes = (tjobs || []).filter((j) => !seen.has(j.id));
      if (newOnes.length > 0) beep();
      // Merge and prune: keep only currently active job ids
      const currentIds = new Set((tjobs || []).map((j) => j.id));
      newOnes.forEach((j) => seen.add(j.id));
      // Prune ids that are no longer active (completed/reassigned away) — allows re-alert if job re-assigned later
      const pruned = Array.from(seen).filter((id) => currentIds.has(id));
      try { localStorage.setItem("yh_seen_tjobs", JSON.stringify(pruned)); } catch { /* silent */ }
      setTransportJobs(tjobs || []);
      if (!active && pending.length > 0) {
        const p = pending[0];
        if (lastIncomingIdRef.current !== p.id) {
          lastIncomingIdRef.current = p.id;
          beep();
        }
        setIncoming(p);
      } else {
        setIncoming(null);
      }
    } catch (err) {
      // Poll loop — log rather than spam UI toasts.
      console.warn("[DriverPage.pollRequests]", err?.response?.status || err?.message);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "driver") { nav("/"); return; }
    api.get("/driver/me").then(({ data }) => {
      setInfo(data);
      setOnline(!!data.vehicle?.is_online);
      if (data.vehicle?.last_location) {
        setLastLoc([data.vehicle.last_location.latitude, data.vehicle.last_location.longitude]);
        setLastAt(data.vehicle.last_location_at);
      }
    });
    pollRequests();
    pollRef.current = setInterval(pollRequests, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [user, loading, nav]);

  const startTracking = () => {
    if (!navigator.geolocation) return toast.error("GPS desteklenmiyor");
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (gpsWatchdogRef.current) clearInterval(gpsWatchdogRef.current);
    const push = (p) => {
      const lat = p.coords.latitude, lng = p.coords.longitude;
      setLastLoc([lat, lng]);
      const now = new Date().toISOString();
      setLastAt(now);
      lastPushRef.current = Date.now();
      api.post("/driver/location", {
        latitude: lat, longitude: lng,
        accuracy: p.coords.accuracy, heading: p.coords.heading, speed: p.coords.speed,
      }).catch(() => { /* silent */ });
    };
    const startWatch = () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = navigator.geolocation.watchPosition(push, (err) => {
        console.warn("geo err", err);
      }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    };
    startWatch();
    // Safety poll every 20s
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(push, () => { /* silent */ }, { enableHighAccuracy: true, timeout: 15000 });
    }, 20000);
    // Watchdog: if no fresh fix for 60s, restart the watcher and try one more getCurrentPosition
    gpsWatchdogRef.current = setInterval(() => {
      const idle = Date.now() - (lastPushRef.current || 0);
      if (idle > 60000) {
        console.warn("[GPS] Stall detected — restarting watcher");
        startWatch();
        navigator.geolocation.getCurrentPosition(push, () => { /* silent */ }, { enableHighAccuracy: true, timeout: 15000 });
      }
    }, 15000);
  };

  const stopTracking = () => {
    if (watchIdRef.current) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (gpsWatchdogRef.current) { clearInterval(gpsWatchdogRef.current); gpsWatchdogRef.current = null; }
    lastPushRef.current = 0;
  };

  useEffect(() => () => stopTracking(), []);

  const goOnline = async () => {
    if (!navigator.geolocation) return toast.error("GPS desteklenmiyor");
    // Ask for permission and take first fix before switching
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        await api.post("/driver/status", null, { params: { is_online: true } });
        setOnline(true);
        toast.success("HATTASIN — konum paylaşılıyor");
        await api.post("/driver/location", {
          latitude: p.coords.latitude, longitude: p.coords.longitude,
          accuracy: p.coords.accuracy, heading: p.coords.heading, speed: p.coords.speed,
        }).catch(() => {});
        setLastLoc([p.coords.latitude, p.coords.longitude]);
        setLastAt(new Date().toISOString());
        startTracking();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Hataya geçiş başarısız");
      }
    }, () => toast.error("GPS izni gerekli"), { enableHighAccuracy: true, timeout: 15000 });
  };

  const goOffline = async () => {
    try {
      await api.post("/driver/status", null, { params: { is_online: false } });
      setOnline(false);
      stopTracking();
      toast.info("Pasif duruma geçtiniz");
    } catch { toast.error("İşlem başarısız"); }
  };

  if (!info) return <div className="p-8 text-center text-muted-foreground">Yükleniyor...</div>;

  const v = info.vehicle;

  const acceptRequest = async () => {
    if (!incoming || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/help-requests/${incoming.id}/accept`);
      toast.success("Talebi kabul ettiniz");
      setActiveMission(data);
      setIncoming(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kabul başarısız");
      pollRequests();
    } finally { setBusy(false); }
  };
  const rejectRequest = async () => {
    if (!incoming || busy) return;
    setBusy(true);
    try {
      await api.post(`/driver/help-requests/${incoming.id}/reject`);
      toast.info("Reddedildi");
      setIncoming(null);
    } catch (e) { toast.error("Reddedilemedi"); }
    finally { setBusy(false); }
  };
  const markArrived = async () => {
    if (!activeMission || busy) return;
    setBusy(true);
    try { await api.post(`/driver/help-requests/${activeMission.id}/arrived`); toast.success("Ulaştınız"); pollRequests(); }
    catch { toast.error("Başarısız"); }
    finally { setBusy(false); }
  };
  const markCompleted = async () => {
    if (!activeMission || busy) return;
    if (activeMission.agreed_price == null) {
      if (!window.confirm("Anlaşılan Fiyat henüz girilmedi. Yine de tamamlansın mı?")) return;
    }
    setBusy(true);
    try { await api.post(`/driver/help-requests/${activeMission.id}/complete`); toast.success("Görev tamamlandı"); setActiveMission(null); pollRequests(); }
    catch { toast.error("Başarısız"); }
    finally { setBusy(false); }
  };

  const distTo = (lat, lng) => {
    if (!lastLoc || lat == null) return null;
    const [a, b] = lastLoc;
    const R = 6371, dLat = (lat - a) * Math.PI / 180, dLon = (lng - b) * Math.PI / 180;
    const A = Math.sin(dLat/2)**2 + Math.cos(a*Math.PI/180)*Math.cos(lat*Math.PI/180)*Math.sin(dLon/2)**2;
    return +(2 * R * Math.asin(Math.sqrt(A))).toFixed(2);
  };

  const advanceTransport = async (job) => {
    if (!job || busy) return;
    const next = DRIVER_NEXT_STATUS[job.status];
    if (!next) return;
    setBusy(true);
    try {
      await api.post(`/transport/requests/${job.id}/status`, null, { params: { status: next.key } });
      toast.success(next.label + " ✓");
      pollRequests();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-[hsl(var(--brand-black))] text-white flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2 font-bold">
          <Truck className="w-5 h-5 text-primary" /> YOL <span className="text-primary">HATTI</span>
        </div>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/giris"); }} className="text-white hover:bg-white/10">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start p-6 text-center gap-6 pt-8">

        {/* ============= AKTİF TAŞIMA GÖREVLERİ (ÇOKLU BAĞIMSIZ KART) ============= */}
        {transportJobs.length > 0 && (
          <div className="w-full max-w-md space-y-3" data-testid="transport-jobs-list">
            <div className="text-primary font-bold text-sm text-center flex items-center gap-2 justify-center">
              <Package className="w-4 h-4" /> AKTİF TAŞIMA GÖREVLERİM ({transportJobs.length})
            </div>
            {transportJobs.map((job) => {
              const next = DRIVER_NEXT_STATUS[job.status];
              return (
                <div key={job.id} className="rounded-2xl bg-primary/15 border-2 border-primary p-4 text-left" data-testid={`transport-job-card-${job.id}`}>
                  <div className="text-primary font-bold text-base flex items-center gap-2 justify-between">
                    <span><Package className="w-4 h-4 inline mr-1" /> {job.pickup_province}/{job.pickup_district} → {job.delivery_province}/{job.delivery_district}</span>
                    <Badge className="bg-primary text-[hsl(var(--brand-black))]" data-testid={`tj-status-${job.id}`}>{TRANSPORT_REQUEST_STATUS_TR[job.status] || job.status}</Badge>
                  </div>
                  <div className="mt-2 text-xs text-white/90 space-y-0.5">
                    <div><b>Yük:</b> {job.transport_category} · {job.quantity} adet {job.make_model ? `· ${job.make_model}` : ""}</div>
                    <div><b>Durum:</b> {job.running_condition}</div>
                    {job.pickup_address && <div><b>Alım Adresi:</b> {job.pickup_address}</div>}
                    {job.delivery_address && <div><b>Teslim Adresi:</b> {job.delivery_address}</div>}
                    {job.assigned_vehicle && <div><b>Araç:</b> {job.assigned_vehicle.name} · {job.assigned_vehicle.vehicle_type}</div>}
                    {job.accepted_pickup_at && <div><b>Alım:</b> {new Date(job.accepted_pickup_at).toLocaleString("tr-TR")}</div>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {job.pickup_lat != null && (
                      <Button asChild variant="outline" size="sm" className="rounded-full h-9" data-testid={`tj-dir-pickup-${job.id}`}>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${job.pickup_lat},${job.pickup_lng}`} target="_blank" rel="noopener noreferrer">
                          <Navigation className="w-3 h-3 mr-1" /> Alım
                        </a>
                      </Button>
                    )}
                    {job.delivery_lat != null && (
                      <Button asChild variant="outline" size="sm" className="rounded-full h-9" data-testid={`tj-dir-delivery-${job.id}`}>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${job.delivery_lat},${job.delivery_lng}`} target="_blank" rel="noopener noreferrer">
                          <Navigation className="w-3 h-3 mr-1" /> Teslim
                        </a>
                      </Button>
                    )}
                  </div>
                  {next && (
                    <Button onClick={() => advanceTransport(job)} disabled={busy} data-testid={`tj-advance-${job.id}`} className="w-full mt-2 rounded-full h-10 bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold text-sm">
                      {next.label}
                    </Button>
                  )}
                  {job.status === "DELIVERED" && (
                    <div className="text-xs text-white/70 text-center italic mt-2">Müşteri onayı bekleniyor…</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* INCOMING REQUEST OVERLAY */}
        {incoming && !activeMission && (
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--success))]/10 border-2 border-[hsl(var(--success))] p-4 shadow-xl animate-pulse-once" data-testid="incoming-request">
            <div className="flex items-center gap-2 justify-center text-[hsl(var(--success))] font-bold text-lg">
              <Bell className="w-6 h-6 animate-bounce" /> YENİ YARDIM TALEBİ
            </div>
            <div className="mt-2 text-sm text-white/90">
              <div className="text-2xl font-bold text-primary">{incoming.distance_km != null ? `${incoming.distance_km} km` : (distTo(incoming.customer_lat, incoming.customer_lng) != null ? `${distTo(incoming.customer_lat, incoming.customer_lng)} km` : "—")} uzakta</div>
              <div className="mt-1">Müşteri: <b>{incoming.customer_name || "—"}</b></div>
              {incoming.customer_phone && (
                <div>Telefon: <a href={`tel:${incoming.customer_phone}`} className="font-bold underline text-primary" data-testid="incoming-phone">{incoming.customer_phone}</a></div>
              )}
              <div className="mt-1">Araç: <b>{incoming.customer_vehicle_type || "—"}</b></div>
              <div>Talep: <b>{incoming.request_type || "—"}</b></div>
              {incoming.customer_vehicle_model && <div className="text-xs">Model: {incoming.customer_vehicle_model}</div>}
              {incoming.description && <div className="text-xs italic mt-1">"{incoming.description}"</div>}
              <div className="text-xs text-white/60 mt-2">Müşteri ile önce iletişime geçin; kabul sonrası tam konum açılacaktır.</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button data-testid="incoming-reject" onClick={rejectRequest} disabled={busy} className="h-14 rounded-2xl bg-destructive hover:bg-destructive/90 text-white text-lg font-bold press">
                <XCircle className="w-5 h-5 mr-1" /> REDDET
              </Button>
              <Button data-testid="incoming-accept" onClick={acceptRequest} disabled={busy} className="h-14 rounded-2xl bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white text-lg font-bold press">
                <CheckCircle2 className="w-5 h-5 mr-1" /> KABUL ET
              </Button>
            </div>
          </div>
        )}

        {/* ACTIVE MISSION CARD */}
        {activeMission && (
          <div className="w-full max-w-md rounded-2xl bg-primary/15 border-2 border-primary p-4" data-testid="active-mission">
            <div className="text-primary font-bold text-lg text-center flex items-center gap-2 justify-center"><Radio className="w-5 h-5 animate-pulse" /> AKTİF GÖREV</div>
            <div className="mt-2 text-sm text-white/90">
              <div>Müşteri: <b>{[activeMission.customer_name, activeMission.customer_surname].filter(Boolean).join(" ") || "—"}</b></div>
              {activeMission.customer_phone && (
                <div>Telefon: <a href={`tel:${activeMission.customer_phone}`} className="font-bold underline">{activeMission.customer_phone}</a></div>
              )}
              <div>Talep: <b>{activeMission.request_type}</b> · Araç: <b>{activeMission.customer_vehicle_type}</b></div>
              {activeMission.description && <div className="text-xs italic">"{activeMission.description}"</div>}
              <div className="mt-1">Uzaklık: <b>{distTo(activeMission.customer_lat, activeMission.customer_lng) != null ? `${distTo(activeMission.customer_lat, activeMission.customer_lng)} km` : "—"}</b></div>
              <div className="mt-1"><Badge className="bg-[hsl(var(--success))] text-white">{roadsideStatusLabel(activeMission.status)}</Badge></div>
              {activeMission.agreed_price != null && (
                <div className="mt-1 text-primary font-bold">Anlaşılan Fiyat: {Number(activeMission.agreed_price).toLocaleString("tr-TR")} ₺ <span className="text-xs text-white/60 font-normal">(kilitli)</span></div>
              )}
            </div>
            {activeMission.agreed_price == null && (
              <div className="mt-3">
                <AgreedPriceInput mission={activeMission} onSaved={pollRequests} />
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 mt-3">
              <Button asChild variant="outline" className="rounded-full h-11" data-testid="mission-dir">
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${activeMission.customer_lat},${activeMission.customer_lng}`} target="_blank" rel="noopener noreferrer">
                  <Navigation className="w-4 h-4 mr-1" /> Yol Tarifi
                </a>
              </Button>
              {activeMission.status !== "arrived" && (
                <Button onClick={markArrived} disabled={busy} className="rounded-full h-12 bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold" data-testid="mission-arrived">
                  MÜŞTERİYE ULAŞTIM
                </Button>
              )}
              <Button onClick={markCompleted} disabled={busy} className="rounded-full h-12 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90 font-bold" data-testid="mission-complete">
                GÖREVİ TAMAMLA
              </Button>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-widest text-white/60">Şoför</div>
          <div className="text-2xl font-bold">{info.driver.name}</div>
        </div>

        {v ? (
          <>
            <div className="rounded-2xl bg-white/5 border border-white/10 px-6 py-4">
              <div className="text-xs uppercase tracking-widest text-white/60">Atanmış Araç</div>
              <div className="text-xl font-bold mt-1" data-testid="assigned-vehicle-name">{v.name}</div>
              {v.plate && <div className="text-sm text-white/80 mt-0.5" data-testid="assigned-vehicle-plate">{v.plate}</div>}
              <div className="text-xs text-white/60 mt-1">{v.vehicle_type}</div>
              {info.business && <div className="text-xs mt-2 text-primary">{info.business.name}</div>}
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Durum</div>
              {online ? (
                <Badge className="bg-[hsl(var(--success))] text-white text-base py-1 px-3" data-testid="status-online"><Radio className="w-4 h-4 mr-1 animate-pulse" /> HATTASIN</Badge>
              ) : (
                <Badge className="bg-white/20 text-white text-base py-1 px-3" data-testid="status-offline">PASİF</Badge>
              )}
            </div>

            {online ? (
              <Button
                data-testid="go-offline"
                onClick={goOffline}
                className="w-64 h-16 rounded-2xl text-lg bg-destructive hover:bg-destructive/90 text-white font-bold press"
              >
                PASİFE GEÇ
              </Button>
            ) : (
              <Button
                data-testid="go-online"
                onClick={goOnline}
                className="w-64 h-16 rounded-2xl text-lg bg-primary hover:bg-primary/90 text-[hsl(var(--brand-black))] font-bold press"
              >
                AKTİFİM / HATTAYIM
              </Button>
            )}

            <div className="text-xs text-white/60">
              <MapPin className="w-3 h-3 inline mr-1" />
              {lastLoc ? (
                <span data-testid="last-loc">Son konum: {lastLoc[0].toFixed(5)}, {lastLoc[1].toFixed(5)}</span>
              ) : online ? "GPS bekleniyor..." : "Aktif olduğunuzda paylaşılır"}
              {lastAt && <div className="text-white/40 mt-1">{new Date(lastAt).toLocaleTimeString("tr-TR")}</div>}
            </div>
          </>
        ) : (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6 max-w-md">
            <div className="font-semibold">Size henüz araç atanmamış</div>
            <div className="text-sm text-white/70 mt-1">Firma sahibinizden Araçlarım bölümünden size bir araç ataması isteyin.</div>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-white/40 p-3 border-t border-white/10">
        Konumunuz yalnızca AKTİF/HATTA olduğunuzda paylaşılır. Firma yönetim ekranlarına erişiminiz yoktur.
      </div>
      <DriverHistory />
    </div>
  );
}

function DriverHistory() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/driver/help-history");
      setItems(data);
    } catch (err) {
      console.warn("[DriverHistory.load]", err?.response?.status || err?.message);
    }
  };
  return (
    <div className="bg-[hsl(var(--brand-black))] text-white/80 border-t border-white/10">
      <button onClick={() => { const n = !open; setOpen(n); if (n) load(); }} className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold" data-testid="driver-hist-toggle">
        <History className="w-4 h-4" /> {open ? "Görev Geçmişini Gizle" : "Görev Geçmişim"}
      </button>
      {open && (
        <div className="max-w-md mx-auto px-4 pb-4 grid gap-2 text-sm">
          {items.length === 0 && <p className="text-white/50 text-center">Henüz görev kaydı yok.</p>}
          {items.map((h) => (
            <div key={h.id} className="rounded-xl bg-white/5 p-3" data-testid={`drv-hist-${h.id}`}>
              <div className="text-xs text-white/50">{new Date(h.created_at).toLocaleString("tr-TR")}</div>
              <div className="font-semibold">{h.request_type} · {h.customer_vehicle_type}</div>
              <div className="text-xs">Durum: <b>{h.status}</b>{h.review ? ` · ⭐ ${h.review.rating}` : ""}</div>
              {h.agreed_price != null && <div className="text-xs text-primary font-bold mt-0.5">Anlaşılan Fiyat: {Number(h.agreed_price).toLocaleString("tr-TR")} ₺</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgreedPriceInput({ mission, onSaved }) {
  const [val, setVal] = useState(mission.agreed_price != null ? String(mission.agreed_price) : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const amount = parseFloat(val);
    if (!isFinite(amount) || amount <= 0) {
      toast.error("Geçerli bir tutar giriniz");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/driver/help-requests/${mission.id}/set-price`, { amount });
      toast.success("Fiyat kaydedildi");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-end gap-2" data-testid="agreed-price-block">
      <div className="flex-1">
        <div className="text-xs text-white/70 mb-1">Anlaşılan Fiyat (₺)</div>
        <input
          data-testid="agreed-price-input"
          type="number"
          min="0"
          step="1"
          inputMode="decimal"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="w-full h-10 rounded-lg bg-white/10 border border-white/20 px-3 text-white"
        />
      </div>
      <Button data-testid="agreed-price-save" onClick={save} disabled={saving} className="h-10 rounded-lg bg-primary text-[hsl(var(--brand-black))] font-bold">
        {saving ? "..." : "Kaydet"}
      </Button>
    </div>
  );
}
